'use client';

import { FC, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { Delete as DeleteIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { useBackend } from '@/hooks/useBackend';
import { useSnackbar } from '@/hooks/useSnackbar';
import { log } from '@/lib/utils/logger';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import type { RecipeFavoriteDto } from '@/api/ApiClient';
import { RecipeFavoriteEditorDialog } from '@/components/dialog/RecipeFavoriteEditorDialog';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';

const OPTION_KEYS: OptionKey[] = ['synthesisTechniques', 'synthesisInstruments'];

/**
 * Recipe Favorites Tab
 *
 * Displays and manages recipe favorites list
 */
export const RecipeFavoritesTab: FC = () => {
  const backend = useBackend();
  const { showSnackbar } = useSnackbar();
  const { options } = useSelectOptions(OPTION_KEYS);

  const [favorites, setFavorites] = useState<RecipeFavoriteDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingFavorite, setEditingFavorite] = useState<RecipeFavoriteDto | null>(null);
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set());
  const [favoritePendingDelete, setFavoritePendingDelete] = useState<RecipeFavoriteDto | null>(null);

  const fetchFavorites = useCallback(async () => {
    return await backend.recipeFavorite.list(null, null, null, null);
  }, [backend]);

  // Load favorites on explicit user-triggered reload paths.
  const loadFavorites = useCallback(async () => {
    setLoading(true);
    try {
      const loadedFavorites = await fetchFavorites();
      setFavorites(loadedFavorites || []);
      setError(null);
    } catch (err: any) {
      log.error('[RecipeFavoritesTab] Error loading favorites:', err);
      setError(getProblemDetail(err) ?? 'Failed to load recipe favorites');
      showSnackbar('Failed to load recipe favorites', 'error');
    } finally {
      setLoading(false);
    }
  }, [fetchFavorites, showSnackbar]);

  useEffect(() => {
    let cancelled = false;

    const loadInitialFavorites = async () => {
      try {
        const loadedFavorites = await fetchFavorites();
        if (cancelled) return;
        setFavorites(loadedFavorites || []);
        setError(null);
      } catch (err: any) {
        if (cancelled) return;
        log.error('[RecipeFavoritesTab] Error loading favorites:', err);
        setError(getProblemDetail(err) ?? 'Failed to load recipe favorites');
        showSnackbar('Failed to load recipe favorites', 'error');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadInitialFavorites();
    return () => {
      cancelled = true;
    };
  }, [fetchFavorites, showSnackbar]);

  // Handle delete
  const deleteFavorite = useCallback(
    async (favorite: RecipeFavoriteDto) => {
      if (!favorite.id) return;
      setDeletingIds((prev) => new Set(prev).add(favorite.id!));
      try {
        await backend.recipeFavorite.delete(favorite.id);
        setFavorites((prev) => prev.filter((f) => f.id !== favorite.id));
        showSnackbar('Recipe favorite deleted', 'success');
      } catch (err: any) {
        log.error('[RecipeFavoritesTab] Error deleting favorite:', err);
        showSnackbar(getProblemDetail(err) ?? 'Failed to delete recipe favorite', 'error');
      } finally {
        setDeletingIds((prev) => {
          const next = new Set(prev);
          next.delete(favorite.id!);
          return next;
        });
      }
    },
    [backend, showSnackbar]
  );

  const handleDelete = useCallback((favorite: RecipeFavoriteDto) => {
    if (!favorite.id) return;
    setFavoritePendingDelete(favorite);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!favoritePendingDelete) return;
    const favorite = favoritePendingDelete;
    setFavoritePendingDelete(null);
    await deleteFavorite(favorite);
  }, [favoritePendingDelete, deleteFavorite]);

  // Handle edit - load full favorite with recipe before opening dialog
  const handleEdit = useCallback(
    async (favorite: RecipeFavoriteDto) => {
      if (!favorite.id) return;

      try {
        // Load full favorite with recipe data before opening dialog
        const fullFavorite = await backend.recipeFavorite.get(favorite.id);
        setEditingFavorite(fullFavorite);
      } catch (err: any) {
        log.error('[RecipeFavoritesTab] Error loading favorite:', err);
        showSnackbar(getProblemDetail(err) ?? 'Failed to load recipe favorite', 'error');
      }
    },
    [backend, showSnackbar]
  );

  // Handle close editor
  const handleCloseEditor = useCallback(
    (saved?: RecipeFavoriteDto) => {
      setEditingFavorite(null);
      if (saved) {
        // Reload favorites to get updated data
        loadFavorites();
      }
    },
    [loadFavorites]
  );

  // Get display labels
  const getTechniqueLabel = (techniqueId?: string) => {
    if (!techniqueId) return '<any>';
    return options.synthesisTechniques?.find((t) => t.id === techniqueId)?.name || techniqueId;
  };

  const getInstrumentLabel = (instrumentId?: string) => {
    if (!instrumentId) return '<any>';
    return options.synthesisInstruments?.find((i) => i.id === instrumentId)?.name || instrumentId;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Technique</TableCell>
              <TableCell>Instrument</TableCell>
              <TableCell>Step</TableCell>
              <TableCell>Template</TableCell>
              <TableCell>Name</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {favorites.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No recipe favorites found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              favorites.map((favorite) => (
                <TableRow key={favorite.id} hover>
                  <TableCell>{getTechniqueLabel(favorite.technique)}</TableCell>
                  <TableCell>{getInstrumentLabel(favorite.instrument)}</TableCell>
                  <TableCell>{favorite.step || 'N/A'}</TableCell>
                  <TableCell>{favorite.template || 'N/A'}</TableCell>
                  <TableCell>{favorite.name || 'N/A'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEdit(favorite)} title="Show Details" color="primary">
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(favorite)}
                      title="Remove"
                      color="error"
                      disabled={deletingIds.has(favorite.id!)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Recipe Favorite Editor Dialog */}
      {editingFavorite ? (
        <RecipeFavoriteEditorDialog open={!!editingFavorite} favorite={editingFavorite} onClose={handleCloseEditor} />
      ) : null}

      <Dialog open={Boolean(favoritePendingDelete)} onClose={() => setFavoritePendingDelete(null)}>
        <DialogTitle>Delete recipe favorite?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`Are you sure you want to delete "${favoritePendingDelete?.name || 'this favorite'}"?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFavoritePendingDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => void handleConfirmDelete()}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
